/* Source: /home/sidsolaiyappan/skooch_ai/shared/learning_dashboard.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { useState, useEffect } from "react";
import { useStoreSlice, getStoreUserId, subscribeStoreUserId, getStoreIsAnonymous, subscribeStoreIsAnonymous, getStoreLearningTick, subscribeStoreLearningTick, getStorePrefFocusPeak, subscribeStorePrefFocusPeak } from "./store.js";
import { getAccessToken } from "./auth_token.js";
import { explainLearnedProfileViaTransport } from "./learned_profile_client.js";
import { computeKeptRateReportViaTransport } from "./schedule_eval_client.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function lxFmt(x, digits) {
  if (((x === undefined) || (x === null))) {
    return "\u2014";
  }
  try {
    return x.toFixed(digits);
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
      return String(x);
    } else {
      throw __jac_e;
    }
  }
}
function lxPct(share) {
  let _s = (((share !== undefined) && (share !== null)) ? share : 0.0);
  let _v = (_s * 100);
  return (_v.toFixed(1) + "%");
}
function lxDeltaText(v) {
  let _n = (((v !== undefined) && (v !== null)) ? v : 0.0);
  if ((_n > 0.0005)) {
    return ("+" + _n.toFixed(2));
  }
  if ((_n < (-0.0005))) {
    return _n.toFixed(2);
  }
  return "\u00b7";
}
function lxDeltaColor(v) {
  let _n = (((v !== undefined) && (v !== null)) ? v : 0.0);
  if ((_n > 0.0005)) {
    return "#2e7d32";
  }
  if ((_n < (-0.0005))) {
    return "#c62828";
  }
  return "var(--text-muted)";
}
function lxMult(v) {
  if (((v === undefined) || (v === null))) {
    return "\u2014";
  }
  let _r = Math.round(v);
  let _diff = (v - _r);
  if ((_diff < 0)) {
    _diff = (0 - _diff);
  }
  if ((_diff < 0.0001)) {
    return ("\u00d7" + String(_r));
  }
  return ("\u00d7" + lxFmt(v, 2));
}
function lxRelTime(iso, nowMs) {
  if (!iso) {
    return "\u2014";
  }
  let _thenMs = 0;
  try {
    let _dt = Reflect.construct(Date, [iso]);
    _thenMs = _dt.getTime();
  } catch (__jac_e) {
    if (true) {
      let _e = __jac_e;
      return String(iso);
    } else {
      throw __jac_e;
    }
  }
  if (!(_thenMs === _thenMs)) {
    return String(iso);
  }
  let _mins = Math.floor(((nowMs - _thenMs) / 60000));
  if ((_mins < 1)) {
    return "just now";
  }
  if ((_mins < 60)) {
    return (String(_mins) + "m ago");
  }
  let _hrs = Math.floor((_mins / 60));
  if ((_hrs < 24)) {
    return (String(_hrs) + "h ago");
  }
  let _days = Math.floor((_hrs / 24));
  return (String(_days) + "d ago");
}
function LxMeter(props) {
  if (!_jac.builtin.bool(props)) {
    props = {};
  }
  let _label = ((props["label"] !== undefined) ? props["label"] : "");
  let _value = (((props["value"] !== undefined) && (props["value"] !== null)) ? props["value"] : 0.0);
  let _pctW = (_value * 100);
  if ((_pctW < 0)) {
    _pctW = 0;
  }
  if ((_pctW > 100)) {
    _pctW = 100;
  }
  return __jacJsx("div", {"className": "lx-meter", "style": {"marginTop": "0.4rem"}}, [__jacJsx("div", {"style": {"display": "flex", "justifyContent": "space-between", "fontSize": "0.75rem", "color": "var(--text-secondary)", "marginBottom": "2px"}}, [__jacJsx("span", {}, [_label]), __jacJsx("span", {"style": {"fontVariantNumeric": "tabular-nums"}}, [lxFmt(_value, 3)])]), __jacJsx("div", {"style": {"height": "6px", "background": "var(--border-light)", "borderRadius": "3px", "overflow": "hidden"}}, [__jacJsx("div", {"style": {"height": "100%", "width": (String(_pctW) + "%"), "background": "var(--accent)", "borderRadius": "3px"}}, [])])]);
}
async function fetchLearningExplain(setPayload, setLoading, setError, setLastRefreshed) {
  let _uid = getStoreUserId();
  let _isAnon = false;
  try {
    if (window.__skoochIsAnon) {
      _isAnon = true;
    }
  } catch (__jac_e) {
    if (true) {
      let _aErr = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  if ((!_uid || _isAnon)) {
    setLoading(false);
    return;
  }
  setLoading(true);
  setError("");
  try {
    if (!window.__skoochJacCall) {
      setError("The Jac call bridge is not bound yet \u2014 try Refresh in a moment.");
      setLoading(false);
      return;
    }
    let _qp = getStorePrefFocusPeak();
    let _tok = await getAccessToken();
    let _res = await explainLearnedProfileViaTransport(_uid, _qp, _tok);
    if ((_res && (_res.dist !== undefined))) {
      try {
        let _kr = await computeKeptRateReportViaTransport(_uid, _qp, _tok);
        if ((_kr && (_kr.eras !== undefined))) {
          _res.keptRate = _kr;
        }
      } catch (__jac_e) {
        if (true) {
          let _krErr = __jac_e;
        } else {
          throw __jac_e;
        }
      }
      setPayload(_res);
      let _now = Reflect.construct(Date, []);
      setLastRefreshed(_now.toLocaleTimeString());
    } else {
      let _raw = "";
      try {
        _raw = JSON.stringify(_res).slice(0, 300);
      } catch (__jac_e) {
        if (true) {
          let _jsErr = __jac_e;
          _raw = String(_res);
        } else {
          throw __jac_e;
        }
      }
      setError(("explain_learned_profile returned an unexpected payload (no dist field): " + _raw));
    }
  } catch (__jac_e) {
    if (true) {
      let _feErr = __jac_e;
      setError(("Could not load the learning X-ray: " + String(_feErr)));
    } else {
      throw __jac_e;
    }
  }
  setLoading(false);
}
function LearningDashboardView() {
  let _userId = useStoreSlice(getStoreUserId, subscribeStoreUserId);
  let _isAnon = useStoreSlice(getStoreIsAnonymous, subscribeStoreIsAnonymous);
  let _learningTick = useStoreSlice(getStoreLearningTick, subscribeStoreLearningTick);
  let _prefPeak = useStoreSlice(getStorePrefFocusPeak, subscribeStorePrefFocusPeak);
  let _pPair = useState(null);
  let payload = _pPair[0];
  let setPayload = _pPair[1];
  let _lPair = useState(true);
  let loading = _lPair[0];
  let setLoading = _lPair[1];
  let _ePair = useState("");
  let errMsg = _ePair[0];
  let setError = _ePair[1];
  let _rPair = useState("");
  let lastRefreshed = _rPair[0];
  let setLastRefreshed = _rPair[1];
  useEffect(() => {
    fetchLearningExplain(setPayload, setLoading, setError, setLastRefreshed);
  }, [_learningTick, _userId]);
  let _hasUser = ((_userId && !_isAnon) ? true : false);
  let _card = {"background": "var(--bg-card)", "border": "1px solid var(--border)", "borderRadius": "var(--radius-md)", "padding": "1rem 1.25rem", "marginBottom": "1rem", "boxShadow": "var(--shadow-sm)"};
  let _secTitle = {"fontSize": "0.74rem", "fontWeight": 700, "letterSpacing": "0.09em", "textTransform": "uppercase", "color": "var(--text-muted)", "margin": "0 0 0.6rem 0"};
  let _mono = {"fontFamily": "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", "fontSize": "0.84rem", "color": "var(--text-primary)", "fontVariantNumeric": "tabular-nums"};
  let _monoDim = {"fontFamily": "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", "fontSize": "0.72rem", "color": "var(--text-muted)"};
  let _thL = {"textAlign": "left", "padding": "4px 6px", "borderBottom": "1px solid var(--border)", "fontWeight": 600, "color": "var(--text-secondary)", "whiteSpace": "nowrap"};
  let _thR = {"textAlign": "right", "padding": "4px 6px", "borderBottom": "1px solid var(--border)", "fontWeight": 600, "color": "var(--text-secondary)", "whiteSpace": "nowrap"};
  let _tdL = {"textAlign": "left", "padding": "4px 6px", "borderBottom": "1px solid var(--border-light)", "verticalAlign": "top"};
  let _tdR = {"textAlign": "right", "padding": "4px 6px", "borderBottom": "1px solid var(--border-light)", "verticalAlign": "top", "fontVariantNumeric": "tabular-nums", "fontFamily": "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", "fontSize": "0.76rem"};
  let _main = null;
  if (payload) {
    let p = payload;
    let _nowMs = Date.now();
    let _fpOrder = ["morning", "midday", "afternoon", "evening"];
    let _bucketColors = {"morning": "#C98A1F", "midday": "#4F7A6F", "afternoon": "#5F7A52", "evening": "#8A5F78"};
    let _dist = (p.dist ? p.dist : {});
    let _accum = (p.accumulator ? p.accumulator : {});
    let _events = (p.events ? p.events : []);
    let _prior = (p.prior ? p.prior : {"bucket": "", "strength": 0.0});
    let _gate = (p.gate ? p.gate : {"n_ok": false, "sep_ok": false, "open": false});
    let _conf = (p.confidence ? p.confidence : {"value": 0.0, "raw": 0.0, "formula": ""});
    let _fam = (p.familiarity ? p.familiarity : {"value": 0.0, "confidence_term": 0.0, "engagement_term": 0.0, "asks_answered": 0, "formula": ""});
    let _neff = (((p.n_eff !== undefined) && (p.n_eff !== null)) ? p.n_eff : 0.0);
    let _gateN = (((p.gate_threshold !== undefined) && (p.gate_threshold !== null)) ? p.gate_threshold : 3.0);
    let _confHalf = (((p.conf_half !== undefined) && (p.conf_half !== null)) ? p.conf_half : 5.0);
    let _famSat = (((p.fam_sat !== undefined) && (p.fam_sat !== null)) ? p.fam_sat : 8.0);
    let _minn = (((p.min_n !== undefined) && (p.min_n !== null)) ? p.min_n : 5.0);
    let _sepv = (((p.separation !== undefined) && (p.separation !== null)) ? p.separation : 0.0);
    let _sepm = (((p.sep_margin !== undefined) && (p.sep_margin !== null)) ? p.sep_margin : 0.15);
    let _amax = (p.argmax ? p.argmax : "\u2014");
    let _amaxShare = (((p.argmax_share !== undefined) && (p.argmax_share !== null)) ? p.argmax_share : 0.0);
    let _qShare = (((p.quiz_share !== undefined) && (p.quiz_share !== null)) ? p.quiz_share : 0.0);
    let _hl = (p.half_life_days ? p.half_life_days : 35.0);
    let _learned = (p.learned_focus_peak ? p.learned_focus_peak : null);
    let _prevLearned = (p.prev_learned ? p.prev_learned : null);
    let _effective = (_prefPeak ? _prefPeak : _prior.bucket);
    let _consentApplied = (p.consent_applied ? true : false);
    let _appliedBucket = (p.applied_bucket ? p.applied_bucket : _effective);
    let _gateState = "held";
    if ((_consentApplied && (_appliedBucket === _prefPeak))) {
      _gateState = "applied";
    } else if (((_gate.open && _learned) && (_learned === _prefPeak))) {
      _gateState = "applied";
    } else if (_gate.open) {
      _gateState = "proposal";
    }
    let _distSegs = _fpOrder.map((b, _bi) => {
      let _share = (((_jac.poly.getitem(_dist, b) !== undefined) && (_jac.poly.getitem(_dist, b) !== null)) ? _jac.poly.getitem(_dist, b) : 0.0);
      let _acc = (((_jac.poly.getitem(_accum, b) !== undefined) && (_jac.poly.getitem(_accum, b) !== null)) ? _jac.poly.getitem(_accum, b) : 0.0);
      let _isArgmax = (_amax === b);
      let _isQuiz = (_prior.bucket === b);
      let _segGrow = ((_share * 1000) + 1);
      return __jacJsx("div", {"key": ("seg-" + b), "className": ("lx-dist-seg lx-dist-seg-" + b), "data-bucket": b, "data-share": lxFmt(_share, 4), "style": {"flexGrow": _segGrow, "flexBasis": "0", "minWidth": "92px", "padding": "0.5rem 0.55rem", "background": _bucketColors[b], "color": "#FFFFFF", "borderRadius": "6px", "outline": (_isArgmax ? "2px solid var(--text-primary)" : "none"), "outlineOffset": "-2px"}}, [__jacJsx("div", {"style": {"fontSize": "0.72rem", "fontWeight": 700, "textTransform": "uppercase", "letterSpacing": "0.05em", "opacity": 0.95}}, [b, (_isArgmax ? " \u25c6" : ""), (_isQuiz ? " \u00b7 quiz" : "")]), __jacJsx("div", {"style": {"fontSize": "0.84rem", "fontVariantNumeric": "tabular-nums", "marginTop": "2px"}}, [lxFmt(_acc, 2), " \u2192 ", lxPct(_share)])]);
    });
    let _beliefSection = __jacJsx("div", {"className": "lx-belief", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["Focus-peak belief"]), __jacJsx("div", {"style": {"fontSize": "1.05rem", "fontWeight": 600, "color": "var(--text-primary)", "marginBottom": "0.7rem"}}, ["Effective peak now: ", __jacJsx("span", {"className": "lx-effective-peak", "style": {"color": "var(--accent)"}}, [(_effective ? _effective.toUpperCase() : "\u2014")]), __jacJsx("span", {"style": {"fontSize": "0.78rem", "fontWeight": 400, "color": "var(--text-muted)", "marginLeft": "0.5rem"}}, [((_gateState === "applied") ? "learned + confirmed by you" : ((_gateState === "proposal") ? "your setting \u2014 a learned proposal is pending your consent" : "quiz/profile setting (gate held)"))])]), __jacJsx("div", {"className": "lx-dist-bar", "style": {"display": "flex", "gap": "3px", "alignItems": "stretch"}}, [_distSegs]), __jacJsx("div", {"style": {"fontSize": "0.76rem", "color": "var(--text-secondary)", "marginTop": "0.55rem"}}, ["raw accumulator \u2192 normalized share \u00b7 \u25c6 = argmax \u00b7 prior: quiz said ", __jacJsx("strong", {}, [(_prior.bucket ? _prior.bucket.toUpperCase() : "\u2014")]), ", strength ", lxFmt(_prior.strength, 1), " (biases the scores; contributes nothing to n_eff)"])]);
    let _nLine = (((("n_eff = " + lxFmt(_neff, 2)) + (_gate.n_ok ? "  \u2265  " : "  <  ")) + lxFmt(_gateN, 1)) + (_gate.n_ok ? "   \u2713" : "   \u2717"));
    let _sepLine = (((((((("separation = argmax_share \u2212 quiz_share = " + lxFmt(_amaxShare, 3)) + " \u2212 ") + lxFmt(_qShare, 3)) + " = ") + lxFmt(_sepv, 3)) + (_gate.sep_ok ? "  \u2265  " : "  <  ")) + lxFmt(_sepm, 2)) + (_gate.sep_ok ? "   \u2713" : "   \u2717"));
    let _mergeNote = null;
    let _mergedBands = (_gate.merged_bands ? _gate.merged_bands : null);
    if ((_mergedBands && (_mergedBands.length === 2))) {
      let _mbA = String(_mergedBands[0]);
      let _mbB = String(_mergedBands[1]);
      let _mbShareA = (((_jac.poly.getitem(_dist, _mbA) !== undefined) && (_jac.poly.getitem(_dist, _mbA) !== null)) ? _jac.poly.getitem(_dist, _mbA) : 0.0);
      let _mbShareB = (((_jac.poly.getitem(_dist, _mbB) !== undefined) && (_jac.poly.getitem(_dist, _mbB) !== null)) ? _jac.poly.getitem(_dist, _mbB) : 0.0);
      _sepLine = (((((((((((((("separation = (" + _mbA) + " + ") + _mbB) + ") \u2212 quiz_share = (") + lxFmt(_mbShareA, 3)) + " + ") + lxFmt(_mbShareB, 3)) + ") \u2212 ") + lxFmt(_qShare, 3)) + " = ") + lxFmt(_sepv, 3)) + (_gate.sep_ok ? "  \u2265  " : "  <  ")) + lxFmt(_sepm, 2)) + (_gate.sep_ok ? "   \u2713" : "   \u2717"));
      _mergeNote = __jacJsx("div", {"className": "lx-gate-merge", "style": {"fontSize": "0.78rem", "color": "var(--text-secondary)", "marginTop": "0.3rem"}}, ["adjacent-band merge: your evidence straddles the ", __jacJsx("strong", {}, [_mbA]), "/", __jacJsx("strong", {}, [_mbB]), " boundary, so the gate weighs the pair together (runner-up holds \u2265 0.35\u00d7 the leader). The distribution above is unchanged."]);
    }
    let _clampNote = null;
    if (((_gate.open && _learned) && (_amax !== _learned))) {
      _clampNote = __jacJsx("div", {"style": {"fontSize": "0.78rem", "color": "var(--accent-warm)", "marginTop": "0.4rem"}}, ["step-clamp: the evidence argmax is ", __jacJsx("strong", {}, [_amax]), ", but beliefs move at most one bucket per refresh \u2014 clamped to ", __jacJsx("strong", {}, [_learned]), (_prevLearned ? ((" (prev: " + _prevLearned) + ")") : "")]);
    }
    let _movedNote = null;
    if (((_learned && _prevLearned) && (_learned !== _prevLearned))) {
      _movedNote = __jacJsx("div", {"style": {"fontSize": "0.78rem", "color": "var(--text-secondary)", "marginTop": "0.3rem"}}, ["belief moved this refresh: ", _prevLearned, " \u2192 ", _learned]);
    }
    let _verdictTxt = "GATE HELD";
    let _verdictColor = "var(--accent-warm)";
    let _verdictSub = null;
    if ((_gateState === "applied")) {
      let _confirmedBucket = (_appliedBucket ? _appliedBucket : _learned);
      _verdictTxt = (("GATE OPEN \u2014 APPLIED (you confirmed " + (_confirmedBucket ? _confirmedBucket.toUpperCase() : "")) + ")");
      _verdictColor = "var(--accent)";
      _verdictSub = __jacJsx("div", {"style": {"fontSize": "0.8rem", "color": "var(--text-secondary)", "marginTop": "0.3rem"}}, ["You confirmed this learned focus time, so it's now your default \u2014 the separation reads 0 below because your setting already matches what Skooch learned."]);
    } else if ((_gateState === "proposal")) {
      _verdictTxt = ("GATE OPEN \u2014 PROPOSAL PENDING: " + (_learned ? _learned.toUpperCase() : String(_amax).toUpperCase()));
      _verdictColor = "var(--accent)";
      _verdictSub = __jacJsx("div", {"style": {"fontSize": "0.8rem", "color": "var(--text-secondary)", "marginTop": "0.3rem"}}, ["Skooch believes ", __jacJsx("strong", {}, [(_learned ? _learned : _amax)]), " works better for you, but your schedule won't change until you confirm \u2014 watch for the consent toast."]);
    } else {
      _verdictSub = __jacJsx("div", {"style": {"fontSize": "0.8rem", "color": "var(--text-secondary)", "marginTop": "0.3rem"}}, ["Skooch keeps using your quiz answer (", (_prior.bucket ? _prior.bucket : "\u2014"), ") until the evidence clears the gate \u2014 both checks above must pass."]);
    }
    let _gateSection = __jacJsx("div", {"className": "lx-gate", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["The gate — does the evidence override the quiz?"]), __jacJsx("div", {"className": "lx-gate-n", "style": _mono}, [_nLine]), __jacJsx("div", {"className": "lx-gate-sep", "style": _mono}, [_sepLine]), _mergeNote, __jacJsx("div", {"className": "lx-gate-verdict", "data-open": (_gate.open ? "1" : "0"), "data-state": _gateState, "style": {"marginTop": "0.55rem", "fontWeight": 700, "fontSize": "0.92rem", "color": _verdictColor}}, [_verdictTxt]), _verdictSub, __jacJsx("div", {"style": {"fontSize": "0.78rem", "color": "var(--text-secondary)", "marginTop": "0.4rem", "fontVariantNumeric": "tabular-nums"}}, ["learned_focus_peak: ", (_learned ? _learned : "none yet (using quiz)"), " \u00b7 prev_learned: ", (_prevLearned ? _prevLearned : "none yet")]), _clampNote, _movedNote]);
    let _confRaw = (((_conf.raw !== undefined) && (_conf.raw !== null)) ? _conf.raw : 0.0);
    let _confVal = (((_conf.value !== undefined) && (_conf.value !== null)) ? _conf.value : 0.0);
    let _confSub = ((((((("confidence = separation \u00d7 n_eff/(n_eff + conf_half) = " + lxFmt(_sepv, 3)) + " \u00d7 ") + lxFmt(_neff, 1)) + "/") + lxFmt((_neff + _confHalf), 1)) + " = ") + lxFmt(_confRaw, 3));
    let _confDamp = (_confVal - _confRaw);
    if ((_confDamp < 0)) {
      _confDamp = (0 - _confDamp);
    }
    if ((_confDamp > 0.0005)) {
      _confSub = ((_confSub + "  \u2192  gate held: \u00d70.5 damp = ") + lxFmt(_confVal, 3));
    }
    let _asks = (((_fam.asks_answered !== undefined) && (_fam.asks_answered !== null)) ? _fam.asks_answered : 0);
    let _famDims = (_fam.dimensions ? _fam.dimensions : null);
    let _famSub = ((((("familiarity = 0.6\u00d7confidence + 0.4\u00d7min(1, n_eff/fam_sat) = " + lxFmt(_fam.confidence_term, 3)) + " + ") + lxFmt(_fam.engagement_term, 3)) + " = ") + lxFmt(_fam.value, 3));
    if ((_famDims && (_famDims.length > 0))) {
      let _famParts = _famDims.map((d, _fpi) => {
        return ((String(d.name) + " ") + lxFmt(d.term, 3));
      });
      _famSub = ((("familiarity = mean(" + _jac.poly.call(_famParts, "join", " \u00b7 ")) + ") = ") + lxFmt(_fam.value, 3));
    }
    let _famDimRows = null;
    let _famMissingNote = null;
    let _dimLabels = {"focus_peak": "focus peak", "duration": "duration sizing", "cat_time_affinity": "category-time affinity", "day_balance": "day balance"};
    if ((_famDims && (_famDims.length > 0))) {
      _famDimRows = _famDims.map((d, _fdi) => {
        let _dl = ((_dimLabels[d.name] !== undefined) ? _dimLabels[d.name] : String(d.name));
        return __jacJsx("div", {"key": ("famdim-" + String(d.name)), "className": "lx-fam-dim", "data-dim": d.name, "style": {"fontFamily": "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", "fontSize": "0.76rem", "color": "var(--text-secondary)", "marginTop": "0.25rem", "fontVariantNumeric": "tabular-nums"}}, [(((((_dl + ": term ") + lxFmt(d.term, 3)) + " (n ") + lxFmt(d.n, 1)) + ")")]);
      });
      let _missingTxt = "";
      for (const _dn of ["focus_peak", "duration", "cat_time_affinity", "day_balance"]) {
        let _found = false;
        for (const _fd of _famDims) {
          if ((_fd.name === _dn)) {
            _found = true;
          }
        }
        if (!_found) {
          let _ml = ((_dimLabels[_dn] !== undefined) ? _dimLabels[_dn] : _dn);
          _missingTxt = ((_missingTxt + (_missingTxt ? ", " : "")) + _ml);
        }
      }
      if (_missingTxt) {
        _famMissingNote = __jacJsx("div", {"className": "lx-fam-missing", "style": {"fontSize": "0.72rem", "color": "var(--text-muted)", "marginTop": "0.25rem"}}, [("no data yet (excluded from the blend, not counted as zero): " + _missingTxt)]);
      }
    }
    let _affirm = (p.affirmations ? p.affirmations : null);
    let _hlLine = (_affirm ? __jacJsx("div", {"className": "lx-hl-eff", "style": {"fontFamily": "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", "fontSize": "0.78rem", "color": "var(--text-secondary)", "marginTop": "0.6rem", "fontVariantNumeric": "tabular-nums"}}, [(((((((("decay half-life: " + lxFmt(_hl, 0)) + "d \u2192 ") + lxFmt(_affirm.half_life_eff, 1)) + "d (") + String(_affirm.count)) + " affirmations in ") + String(_affirm.window_days)) + "d)")]) : null);
    let _kr = (p.keptRate ? p.keptRate : null);
    let _krQ = (((_kr && _kr.eras) && _kr.eras.quiz) ? _kr.eras.quiz : null);
    let _krL = (((_kr && _kr.eras) && _kr.eras.learned) ? _kr.eras.learned : null);
    let _krRow = null;
    if ((((_krQ && _krL) && (_krQ.rate !== null)) && (_krL.rate !== null))) {
      let _qPct = Math.round((_krQ.rate * 100));
      let _lPct = Math.round((_krL.rate * 100));
      let _upTxt = "";
      if ((_kr.uplift !== null)) {
        let _upPts = Math.round((_kr.uplift * 100));
        _upTxt = (((" (" + ((_upPts >= 0) ? "+" : "")) + String(_upPts)) + " uplift)");
      }
      _krRow = __jacJsx("div", {"className": "lx-kept-rate", "style": {"fontFamily": "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", "fontSize": "0.78rem", "color": "var(--text-secondary)", "marginTop": "0.4rem", "fontVariantNumeric": "tabular-nums"}}, [((((("kept-rate: quiz " + String(_qPct)) + "% \u2192 learned ") + String(_lPct)) + "%") + _upTxt)]);
    }
    let _formulaSection = __jacJsx("div", {"className": "lx-formulas", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["Confidence & familiarity \u2014 the formulas"]), __jacJsx("div", {"className": "lx-conf-sub", "style": _mono}, [_confSub]), __jacJsx("div", {"style": _monoDim}, [(_conf.formula ? _conf.formula : "")]), LxMeter({"label": "confidence (gated value)", "value": _confVal}), __jacJsx("div", {"className": "lx-fam-sub", "style": {"fontFamily": "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", "fontSize": "0.84rem", "color": "var(--text-primary)", "fontVariantNumeric": "tabular-nums", "marginTop": "0.8rem"}}, [_famSub]), __jacJsx("div", {"style": _monoDim}, [(_fam.formula ? _fam.formula : "")]), __jacJsx("div", {"style": {"fontSize": "0.76rem", "color": "var(--text-secondary)", "marginTop": "0.2rem"}}, ["asks_answered = ", String(_asks), " (telemetry \u2014 answered questions; no longer in the formula, so noise answers can't inflate familiarity)"]), LxMeter({"label": "focus dim \u2014 confidence term (0.6 \u00d7 confidence)", "value": (_fam.confidence_term ? _fam.confidence_term : 0.0)}), LxMeter({"label": "focus dim \u2014 evidence term (0.4 \u00d7 min(1, n_eff/fam_sat))", "value": (_fam.engagement_term ? _fam.engagement_term : 0.0)}), LxMeter({"label": "familiarity (blend)", "value": (_fam.value ? _fam.value : 0.0)}), _famDimRows, _famMissingNote, _hlLine, _krRow]);
    let _dur = (p.duration ? p.duration : null);
    let _durSection = null;
    if (_dur) {
      let _durCats = _jac.poly.call(Object, "keys", _dur);
      if ((_durCats.length > 0)) {
        let _durRows = _durCats.map((c, _di) => {
          let _entry = (_jac.poly.getitem(_dur, c) ? _jac.poly.getitem(_dur, c) : {});
          let _mult = (((_entry.mult !== undefined) && (_entry.mult !== null)) ? _entry.mult : 100.0);
          let _n = (((_entry.n !== undefined) && (_entry.n !== null)) ? _entry.n : 0);
          let _gated = (_entry.gated ? true : false);
          let _verdict = (_gated ? "applied" : (((("n=" + String(_n)) + "/3 \u2014 need ") + String((3 - _n))) + " more"));
          return __jacJsx("tr", {"key": ("dur-" + String(c)), "className": "lx-duration-row", "data-category": c, "data-gated": (_gated ? "1" : "0")}, [__jacJsx("td", {"style": _tdL}, [c]), __jacJsx("td", {"style": _tdR}, [("\u00d7" + lxFmt((_mult / 100.0), 2))]), __jacJsx("td", {"style": _tdR}, [String(_n)]), __jacJsx("td", {"style": _tdL}, [__jacJsx("span", {"style": {"color": (_gated ? "var(--accent)" : "var(--text-muted)")}}, [_verdict])])]);
        });
        _durSection = __jacJsx("div", {"className": "lx-duration", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["Duration calibration — how Skooch sizes your blocks"]), __jacJsx("div", {"style": {"fontSize": "0.76rem", "color": "var(--text-secondary)", "marginBottom": "0.5rem"}}, ["From your post-completion answers (way-less \u00d70.75 \u00b7 about-right \u00d71.0 \u00b7 way-more \u00d71.35). A category needs 3 answers to apply; below that the optimizer uses \u00d71.00."]), __jacJsx("div", {"style": {"overflowX": "auto"}}, [__jacJsx("table", {"className": "lx-duration-table", "style": {"width": "100%", "borderCollapse": "collapse", "fontSize": "0.8rem", "color": "var(--text-primary)"}}, [__jacJsx("thead", {}, [__jacJsx("tr", {}, [__jacJsx("th", {"style": _thL}, ["category"]), __jacJsx("th", {"style": _thR}, ["multiplier"]), __jacJsx("th", {"style": _thR}, ["answers"]), __jacJsx("th", {"style": _thL}, ["gate"])])]), __jacJsx("tbody", {}, [_durRows])])])]);
      } else {
        _durSection = __jacJsx("div", {"className": "lx-duration lx-duration-empty", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["Duration calibration — how Skooch sizes your blocks"]), __jacJsx("div", {"style": {"fontSize": "0.85rem", "color": "var(--text-secondary)"}}, ["No duration feedback yet — when you finish a scheduled task, Skooch asks how the planned time landed and learns to size your blocks per category."])]);
      }
    }
    let _aff = (p.affinity ? p.affinity : null);
    let _affSection = null;
    if (_aff) {
      let _affCats = _jac.poly.call(Object, "keys", _aff);
      if ((_affCats.length > 0)) {
        let _affRows = _affCats.map((c, _ai) => {
          let _entry = (_jac.poly.getitem(_aff, c) ? _jac.poly.getitem(_aff, c) : {});
          let _pref = (_entry.preferred ? _entry.preferred : null);
          let _n = (((_entry.n !== undefined) && (_entry.n !== null)) ? _entry.n : 0);
          let _gated = (_entry.gated ? true : false);
          _dist = (_entry.dist ? _entry.dist : {});
          let _distTxt = "";
          for (const _b of _fpOrder) {
            let _sv = (((_jac.poly.getitem(_dist, _b) !== undefined) && (_jac.poly.getitem(_dist, _b) !== null)) ? _jac.poly.getitem(_dist, _b) : 0.0);
            _distTxt = ((((_distTxt + _b.slice(0, 3)) + " ") + Math.round((_sv * 100))) + "%  ");
          }
          let _days = (_entry.days ? _entry.days : null);
          let _dayTxt = "";
          if (_days) {
            let _dayNames = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            let _dk = _jac.poly.call(Object, "keys", _days);
            for (const _dn of _dk) {
              let _dvv = _jac.poly.getitem(_days, _dn);
              let _lbl = (((Number(_dn) >= 1) && (Number(_dn) <= 7)) ? _dayNames.at(Number(_dn)) : String(_dn));
              if ((_dvv > 0.05)) {
                _dayTxt = (((_dayTxt + "+") + _lbl) + " ");
              } else if ((_dvv < (-0.05))) {
                _dayTxt = (((_dayTxt + "\u2212") + _lbl) + " ");
              }
            }
          }
          let _verdict = ((_gated && _pref) ? ("\u2192 " + String(_pref)) : (("n=" + String(_n)) + "/3"));
          return __jacJsx("tr", {"key": ("aff-" + String(c)), "className": "lx-affinity-row", "data-category": c, "data-preferred": (_pref ? _pref : ""), "data-gated": (_gated ? "1" : "0")}, [__jacJsx("td", {"style": _tdL}, [c]), __jacJsx("td", {"style": _tdL}, [__jacJsx("span", {"style": {"color": ((_gated && _pref) ? "var(--accent)" : "var(--text-muted)")}}, [_verdict])]), __jacJsx("td", {"style": _tdL}, [__jacJsx("span", {"style": _monoDim}, [_distTxt])]), __jacJsx("td", {"style": _tdL, "className": "lx-daybalance"}, [__jacJsx("span", {"style": _monoDim}, [(_dayTxt ? _dayTxt : "\u00b7")])]), __jacJsx("td", {"style": _tdR}, [String(_n)])]);
        });
        _affSection = __jacJsx("div", {"className": "lx-affinity", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["Category-time affinity — when you like to do each thing"]), __jacJsx("div", {"style": {"fontSize": "0.76rem", "color": "var(--text-secondary)", "marginBottom": "0.5rem"}}, ["From your moves, accepts, and 'this suits that time' answers. A category needs 3 signals AND a clear winner to set a preferred bucket; the optimizer then gives that slot a small bonus. The day column shows learned prefer (+) / avoid (\u2212) weekdays."]), __jacJsx("div", {"style": {"overflowX": "auto"}}, [__jacJsx("table", {"className": "lx-affinity-table", "style": {"width": "100%", "borderCollapse": "collapse", "fontSize": "0.8rem", "color": "var(--text-primary)"}}, [__jacJsx("thead", {}, [__jacJsx("tr", {}, [__jacJsx("th", {"style": _thL}, ["category"]), __jacJsx("th", {"style": _thL}, ["preferred"]), __jacJsx("th", {"style": _thL}, ["distribution"]), __jacJsx("th", {"style": _thL}, ["day balance"]), __jacJsx("th", {"style": _thR}, ["n"])])]), __jacJsx("tbody", {}, [_affRows])])])]);
      } else {
        _affSection = __jacJsx("div", {"className": "lx-affinity lx-affinity-empty", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["Category-time affinity — when you like to do each thing"]), __jacJsx("div", {"style": {"fontSize": "0.85rem", "color": "var(--text-secondary)"}}, ["No category-time signals yet — move tasks around or tell Skooch a task suits a time, and Skooch learns when you prefer each kind of work."])]);
      }
    }
    let _evCount = _events.length;
    let _exCount = 0;
    for (const _e of _events) {
      if (_e.excluded) {
        _exCount = (_exCount + 1);
      }
    }
    let _ledgerRows = _events.map((ev, _ri) => {
      let _isExcluded = (ev.excluded ? true : false);
      let _cancelled = ((!_isExcluded && (ev.modifier === 0)) ? true : false);
      let _d = (ev.delta ? ev.delta : {});
      let _sign = (((ev.sign !== undefined) && (ev.sign !== null)) ? ev.sign : 1.0);
      let _voteTxt = "\u2014";
      if (ev.bucket) {
        _voteTxt = (((_sign > 0) ? "\u25b2 " : "\u25bc ") + String(ev.bucket));
        if (ev.neg_bucket) {
          _voteTxt = (((_voteTxt + " (debit ") + String(ev.neg_bucket)) + ")");
        }
      }
      let _deltaDeco = (_cancelled ? "line-through" : "none");
      if (_isExcluded) {
        return __jacJsx("tr", {"key": ("ev-" + String(_ri)), "className": "lx-ledger-row lx-excluded", "data-event-type": ev.event_type, "style": {"opacity": 0.45}}, [__jacJsx("td", {"style": _tdL, "title": ev.occurred_at}, [lxRelTime(ev.occurred_at, _nowMs)]), __jacJsx("td", {"style": _tdL}, [ev.label]), __jacJsx("td", {"style": _tdL, "colSpan": 10}, [__jacJsx("span", {"style": {"fontStyle": "italic", "color": "var(--text-muted)"}}, ["excluded \u2014 ", (ev.excluded_reason ? ev.excluded_reason : "no usable focus signal")])])]);
      }
      return __jacJsx("tr", {"key": ("ev-" + String(_ri)), "className": "lx-ledger-row", "data-event-type": ev.event_type}, [__jacJsx("td", {"style": _tdL, "title": ev.occurred_at}, [lxRelTime(ev.occurred_at, _nowMs)]), __jacJsx("td", {"style": _tdL}, [ev.label]), __jacJsx("td", {"style": _tdL}, [_voteTxt]), __jacJsx("td", {"style": _tdR}, [lxFmt(ev.base_weight, 0)]), __jacJsx("td", {"style": _tdR}, [lxFmt(ev.decay, 2), " @ ", lxFmt(ev.age_days, 1), "d"]), __jacJsx("td", {"style": _tdR}, [__jacJsx("div", {}, [lxMult(ev.modifier)]), (ev.modifier_source ? __jacJsx("div", {"style": {"fontSize": "0.66rem", "color": "var(--text-muted)", "maxWidth": "150px", "whiteSpace": "normal"}}, [ev.modifier_source]) : null)]), __jacJsx("td", {"style": _tdR}, [lxFmt((((ev.weak !== undefined) && (ev.weak !== null)) ? ev.weak : 1.0), 1)]), __jacJsx("td", {"style": _tdR}, [__jacJsx("span", {"style": {"color": lxDeltaColor(_d.morning), "textDecoration": _deltaDeco}}, [lxDeltaText(_d.morning)])]), __jacJsx("td", {"style": _tdR}, [__jacJsx("span", {"style": {"color": lxDeltaColor(_d.midday), "textDecoration": _deltaDeco}}, [lxDeltaText(_d.midday)])]), __jacJsx("td", {"style": _tdR}, [__jacJsx("span", {"style": {"color": lxDeltaColor(_d.afternoon), "textDecoration": _deltaDeco}}, [lxDeltaText(_d.afternoon)])]), __jacJsx("td", {"style": _tdR}, [__jacJsx("span", {"style": {"color": lxDeltaColor(_d.evening), "textDecoration": _deltaDeco}}, [lxDeltaText(_d.evening)])]), __jacJsx("td", {"style": _tdR}, [lxFmt(ev.n_contrib, 2)])]);
    });
    let _ledgerSection = __jacJsx("div", {"className": "lx-ledger-card", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["Evidence ledger — every event the model weighs"]), __jacJsx("div", {"style": {"fontSize": "0.76rem", "color": "var(--text-secondary)", "marginBottom": "0.5rem", "fontVariantNumeric": "tabular-nums"}}, [String(_evCount), " events fetched \u00b7 ", String((_evCount - _exCount)), " contribute \u00b7 ", String(_exCount), " excluded \u00b7 each row: \u0394 = sign \u00d7 base_weight \u00d7 decay \u00d7 modifier \u00d7 weak"]), ((_evCount === 0) ? __jacJsx("div", {"className": "lx-ledger-empty", "style": {"fontSize": "0.85rem", "color": "var(--text-secondary)", "padding": "0.6rem 0"}}, ["No interaction events captured yet — every move, accept, reject, and toast answer will appear here as a weighted vote."]) : __jacJsx("div", {"style": {"overflowX": "auto"}}, [__jacJsx("table", {"className": "lx-ledger", "style": {"width": "100%", "borderCollapse": "collapse", "fontSize": "0.78rem", "color": "var(--text-primary)"}}, [__jacJsx("thead", {}, [__jacJsx("tr", {}, [__jacJsx("th", {"style": _thL}, ["when"]), __jacJsx("th", {"style": _thL}, ["event"]), __jacJsx("th", {"style": _thL}, ["vote"]), __jacJsx("th", {"style": _thR}, ["w"]), __jacJsx("th", {"style": _thR}, ["decay"]), __jacJsx("th", {"style": _thR}, ["mod"]), __jacJsx("th", {"style": _thR}, ["weak"]), __jacJsx("th", {"style": _thR}, ["Δ morn"]), __jacJsx("th", {"style": _thR}, ["Δ mid"]), __jacJsx("th", {"style": _thR}, ["Δ aft"]), __jacJsx("th", {"style": _thR}, ["Δ eve"]), __jacJsx("th", {"style": _thR}, ["n"])])]), __jacJsx("tbody", {}, [_ledgerRows])])]))]);
    let _legendSection = __jacJsx("div", {"className": "lx-legend", "style": _card}, [__jacJsx("h2", {"style": _secTitle}, ["How learning works here"]), __jacJsx("ul", {"style": {"margin": "0", "paddingLeft": "1.1rem", "fontSize": "0.79rem", "color": "var(--text-secondary)", "lineHeight": 1.55}}, [__jacJsx("li", {}, ["Signal weights (precision tags): preference / feedback answer 12 \u00b7 placement rejected 4 \u00b7 placement moved 3 \u00b7 placement accepted 1 (\u00d70.3 passive-accept damp)."]), __jacJsx("li", {}, ["Recency: every vote decays with a ", lxFmt(_hl, 0), "-day half-life \u2014 decay = 0.5^(age_days / ", lxFmt(_hl, 0), ")."]), __jacJsx("li", {}, ["Attribution multipliers from your toast answers: 'confirm' \u00d74 \u00b7 'just this once' \u00d70 \u00b7 'was blocked' \u00d70 \u00b7 'task-affinity' \u2192 routed to category affinity instead of focus-peak \u00b7 'Wrong day for this' (wrong_day) \u00d70 for focus today \u2014 captured as a seed for the future day-balance learner."]), __jacJsx("li", {}, ["Moves also debit the bucket you moved away from (\u22120.5 \u00d7 weight) \u2014 a move is evidence for the destination and against the origin."]), __jacJsx("li", {}, ["Completions are captured but never teach focus (confounded: the slot was the optimizer's own choice, not yours)."]), __jacJsx("li", {}, ["The quiz prior biases the raw scores but earns no n_eff \u2014 the gate must be cleared by real interaction evidence."])])]);
    _main = __jacJsx("div", {"className": "lx-sections"}, [_beliefSection, _gateSection, _formulaSection, _durSection, _affSection, _ledgerSection, _legendSection]);
  }
  let _body = null;
  if (!_hasUser) {
    _body = __jacJsx("div", {"className": "lx-empty", "style": _card}, [__jacJsx("div", {"style": {"fontSize": "1.0rem", "fontWeight": 600, "color": "var(--text-primary)", "marginBottom": "0.4rem"}}, ["Learning is available once you have an account"]), __jacJsx("div", {"style": {"fontSize": "0.85rem", "color": "var(--text-secondary)"}}, ["Skooch learns from what you do, not just what you say — moves, accepts, rejects, and your toast answers all become weighted evidence. Save your account to start the ledger."])]);
  } else if ((loading && !payload)) {
    _body = __jacJsx("div", {"className": "lx-loading", "style": _card}, [__jacJsx("div", {"style": {"fontSize": "0.9rem", "color": "var(--text-secondary)"}}, ["Reading the evidence ledger…"])]);
  } else if ((errMsg && !payload)) {
    _body = __jacJsx("div", {"className": "lx-error", "style": _card}, [__jacJsx("div", {"style": {"fontSize": "0.85rem", "color": "#c62828", "marginBottom": "0.5rem"}}, [errMsg]), __jacJsx("button", {"className": "btn btn-sm btn-secondary", "onClick": () => {
      fetchLearningExplain(setPayload, setLoading, setError, setLastRefreshed);
    }}, ["Retry"])]);
  } else {
    _body = _main;
  }
  let _staleWarn = null;
  if ((errMsg && payload)) {
    _staleWarn = __jacJsx("div", {"className": "lx-stale-warn", "style": {"fontSize": "0.76rem", "color": "var(--accent-warm)", "marginBottom": "0.6rem"}}, ["Refresh failed \u2014 showing the last good payload. ", errMsg]);
  }
  return __jacJsx("div", {"className": "learning-xray", "data-loaded": (payload ? "1" : "0"), "style": {"maxWidth": "1100px", "margin": "0 auto", "padding": "1.25rem 1.5rem 3rem 1.5rem"}}, [__jacJsx("div", {"className": "lx-header", "style": {"display": "flex", "alignItems": "flex-end", "justifyContent": "space-between", "marginBottom": "1rem", "gap": "1rem"}}, [__jacJsx("div", {}, [__jacJsx("h1", {"style": {"margin": "0", "fontSize": "1.35rem", "color": "var(--text-primary)"}}, ["Learning X-ray"]), __jacJsx("div", {"style": {"fontSize": "0.82rem", "color": "var(--text-secondary)", "marginTop": "2px"}}, ["Every number the learner uses — the evidence, the weights, the gate. Nothing dumbed down."])]), __jacJsx("div", {"style": {"display": "flex", "alignItems": "center", "gap": "0.6rem", "flexShrink": 0}}, [(lastRefreshed ? __jacJsx("span", {"className": "lx-refreshed-at", "style": {"fontSize": "0.72rem", "color": "var(--text-muted)", "fontVariantNumeric": "tabular-nums"}}, ["refreshed ", lastRefreshed]) : null), __jacJsx("button", {"className": "btn btn-sm btn-secondary lx-refresh", "disabled": ((loading && _hasUser) ? true : false), "onClick": () => {
    fetchLearningExplain(setPayload, setLoading, setError, setLastRefreshed);
  }}, [((loading && _hasUser) ? "Refreshing\u2026" : "Refresh")])])]), _staleWarn, _body]);
}
export {LearningDashboardView, LxMeter, fetchLearningExplain, lxDeltaColor, lxDeltaText, lxFmt, lxMult, lxPct, lxRelTime};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:lxFmt": {"is_writer": false, "touches": ["*"]}, "func:lxPct": {"is_writer": false, "touches": ["*"]}, "func:lxDeltaText": {"is_writer": false, "touches": ["*"]}, "func:lxDeltaColor": {"is_writer": false, "touches": ["*"]}, "func:lxMult": {"is_writer": false, "touches": ["*"]}, "func:lxRelTime": {"is_writer": false, "touches": ["*"]}, "func:LxMeter": {"is_writer": false, "touches": ["*"]}, "func:fetchLearningExplain": {"is_writer": true, "touches": ["*"]}, "func:LearningDashboardView": {"is_writer": true, "touches": ["*"]}}); };
//# sourceMappingURL=learning_dashboard.js.map
